
import React from "react";
import { ErrorBoundary } from "@/components/ErrorBoundary";

// Import debug component for development
const AuthDebugInfo = React.lazy(() => import("@/components/debug/AuthDebugInfo"));

// Import components with error boundaries
const GlassmorphicHero = React.lazy(() => import("@/components/glassmorphic/hero/GlassmorphicHero"));
const GlassmorphicFeatures = React.lazy(() => import("@/components/glassmorphic/features/GlassmorphicFeaturesContainer"));
const GlassmorphicHowItWorks = React.lazy(() => import("@/components/glassmorphic/GlassmorphicHowItWorks"));
const GlassmorphicPricing = React.lazy(() => import("@/components/glassmorphic/GlassmorphicPricing"));
const GlassmorphicTestimonials = React.lazy(() => import("@/components/glassmorphic/GlassmorphicTestimonials"));
const GlassmorphicDashboardShowcase = React.lazy(() => import("@/components/glassmorphic/GlassmorphicDashboardShowcase"));
const GlassmorphicAIAnalysisShowcase = React.lazy(() => import("@/components/glassmorphic/GlassmorphicAIAnalysisShowcase"));
const FAQContainer = React.lazy(() => import("@/components/glassmorphic/faq/FAQContainer"));
const HomepageLayout = React.lazy(() => import("@/modules/homepage/components/layout/HomepageLayout").then(m => ({ default: m.default })));

// Simple fallback component for errors
const ErrorFallback = () => (
  <div className="p-4 bg-red-50 text-red-700 rounded-lg my-4">
    <h2 className="font-bold text-lg mb-2">Component Error</h2>
    <p className="mb-2">This component failed to load.</p>
  </div>
);

// Wrapper component with error boundary and suspense
const ComponentWithErrorBoundary = ({ children }: { children: React.ReactNode }) => (
  <ErrorBoundary 
    fallback={
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center p-8 max-w-2xl">
          <h1 className="text-2xl font-bold text-destructive mb-4">Something went wrong</h1>
          <p className="text-muted-foreground mb-6">
            We're having trouble loading the homepage. Please check the console for more details.
          </p>
          <button 
            onClick={() => window.location.reload()} 
            className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
          >
            Reload Page
          </button>
        </div>
      </div>
    }
  >
    <React.Suspense fallback={<div className="min-h-screen flex items-center justify-center">Loading...</div>}>
      {children}
    </React.Suspense>
  </ErrorBoundary>
);

// Helper component to wrap components with error boundary
const SafeComponent = ({ children }: { children: React.ReactNode }) => (
  <ErrorBoundary fallback={<ErrorFallback />}>
    {children}
  </ErrorBoundary>
);

// Define the component
const HomeComponent = () => {
  React.useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('Rendering Home component');
    }
  });
  
  return (
    <ComponentWithErrorBoundary>
      {/* Debug component for development */}
      <React.Suspense fallback={null}>
        <AuthDebugInfo />
      </React.Suspense>

      <HomepageLayout showParticles={true}>
        <SafeComponent>
          <GlassmorphicHero />
        </SafeComponent>
        
        <div id="dashboard-showcase">
          <SafeComponent>
            <GlassmorphicDashboardShowcase />
          </SafeComponent>
        </div>
        
        <div id="features">
          <SafeComponent>
            <GlassmorphicFeatures />
          </SafeComponent>
        </div>
        
        <div id="how-it-works">
          <SafeComponent>
            <GlassmorphicHowItWorks />
          </SafeComponent>
        </div>
        
        <div id="ai-showcase">
          <SafeComponent>
            <GlassmorphicAIAnalysisShowcase />
          </SafeComponent>
        </div>
        
        <div id="testimonials">
          <SafeComponent>
            <GlassmorphicTestimonials />
          </SafeComponent>
        </div>
        
        <div id="pricing">
          <SafeComponent>
            <GlassmorphicPricing />
          </SafeComponent>
        </div>
        
        <div id="faq">
          <SafeComponent>
            <FAQContainer items={[]} />
          </SafeComponent>
        </div>
      </HomepageLayout>
    </ComponentWithErrorBoundary>
  );
};

// Add display name for better debugging
HomeComponent.displayName = 'Home';

// Memoize the component to prevent unnecessary re-renders
export default React.memo(HomeComponent);
